<template>
    <div class="m-top-20 panel panel-danger">
        <div v-if="showLink" class="modalDialog is-active" role="dialog" aria-labelledby="modal-title" aria-describedby="modal-description" aria-hidden="true" @keyup.esc="modalClose">
            <div class="modal-contents">
                <div>
                    <a title="Close" class="close" @click="showLink=false">X</a>
                    <h3><slot id="modal-title" name="header">View Paystub</slot></h3>
                    <hr />
                </div>
                <div>
                    <slot id="modal-description" name="body">
                        <span class="bigger">
                            Click <a target="_edits" :href="'/oasis/viewer/view/archive/' + this.link" @click="showLink=false">HERE</a> to View.
                        </span>
                    </slot>
                    <hr />
                </div>
                <div class="right">
                    <button class="btn btn-primary" @click="showLink=false">Close</button>
                </div>
                <div class="clear"></div>
            </div>
        </div>
        <div class="panel-heading centered bold big ">
            <div class="col-xs-4 centered bigger">
                Paystubs
            </div>
            <div class="right" style="display: inline-flex;">
                <span class="p-right-5"><div v-if="loading" class="inline-loader"></div></span>
            </div>
            <div class="clear"></div>
        </div>
        <ag-grid
            :name="name"
            :ref="reference"
            :iteration="iteration"
            :width="width"
            :height="height"
            :columns="columnDefs"
            :row-data="rowData"
            :options="gridOptions"
            sidebar="filters"
            :filter-order="filterOrder"
            :classes="'ag-theme-alpine ' + (loading ? 'opacity-25' : '')"
        />
    </div>
</template>

<script>
import 'regenerator-runtime/runtime';
import Axios from '../classes/Axios.js';
import DateField from './DateField.vue';
import AgGrid from './AgGrid.vue';
import {defineComponent} from 'vue';

export default defineComponent ({
    name: "Paystubs",

    components: {
        'date-field': DateField,
        'ag-grid': AgGrid,
    },

    props: {
        type: {
            type: String,
            required: false,
            default: 'employee'
        },
        employeeId: {
            type: [String, Number],
            required: false,
        },
        entityId: {
            type: [String, Number],
            required: false,
        },
        name: {
            type: String,
            required: false,
            default: 'paystubs',
        },
        reference: {
            type: String,
            required: false,
            default: 'paystubs',
        },
        classes: {
            type: [ String, Array ],
            required: false,
            default: 'form-control'
        },
    },

    data() {
        return {
            waiting: false, // whole-page spinner
            loading: false, // spinner
            iteration: 0,
            proPayPayrollId: null,
            searchQueryDateStart: undefined,
            searchQueryDateEnd: undefined,
            locale: window.navigator.userLanguage || window.navigator.language || 'en-US', // 'fr-fr' // test stuff
            oasisApi: new Axios('/restv2'),
            dateFormat: 'MM/dd/yyyy',
            link: '',
            showLink: false,

            // AG Grid
            rowData: [],
            columnDefs: undefined,
            sidebar: undefined,
            filterOrder: undefined,
            gridOptions: {
                singleClickEdit: false,
                undoRedoCellEditing: false,
                enterMovesDown: false,
                enterMovesDownAfterEdit: false,
                rowSelection: false, // 'multiple',
                suppressContextMenu: true,
                defaultColDef: {
                    resizable: true,
                    sortable: true,
                    menuTabs: [],
                    suppressSizeToFit: false,
                    headerCheckboxSelectionFilteredOnly: true,
                    filterParams: {
                        suppressMiniFilter: true,
                        defaultToNothingSelected: true,
                        buttons: ['reset', 'apply'],
                        debounceMs: 200
                    }
                },
                loadingOverlayComponentParams: {
                    loadingMessage: 'One moment please...',
                },
                onGridReady: (event) => this.$refs[this.reference].onGridReady(event),
                onCellClicked: (event) => {
                    // if clicked on first row, load the edit
                    if (event.column.getColId() === 'date' && event && event.data && event.data.payroll) {
                        this.getPaystub(event.data.payroll, event.data.assignment, event.data.rendered_paystub);
                    }
                },
            }
        }
    },

    computed: {
        width() {
            // Reducing by 3 % to handle sigma-frontend navbar
            return '100%';
        },

        height() {
            // Reducing by 3 % to handle sigma-frontend navbar
            return window.innerHeight * 0.63 + 'px';
        },

        today() {
            let date = new Date();
            var day = date.getDate();
            var monthIndex = date.getMonth();
            var year = date.getFullYear();

            return year + '-' + this.pad(monthIndex * 1 + 1) + '-' + this.pad(day);
        },
    },

    methods: {
        async getPaystubs() {
            this.loading = true;
            this.rowData = undefined;
            const endpoint = this.type === 'employer' ? '/employee/paystubs' : '/employee/paystubs';
            let query = {};

            if (this.employeeId) query.employee = this.employeeId;
            if (this.searchQueryDateStart) query.payroll__check_date__ge = new Date(this.searchQueryDateStart).toISOString().split('T')[0];
            if (this.searchQueryDateEnd) query.payroll__check_date__le = new Date(this.searchQueryDateEnd).toISOString().split('T')[0];

            if (Object.keys(query).length < 2) {
                this.createDialog({ "title": "Error", "body": 'Please enter a search option such as Employee or a date range', "buttonNo": false, "buttonYesLabel": "OK" });
                this.loading = false;
                return false;
            }

            let queryString = Object.keys(query).map(key => key + '=' + query[key]).join('&');
            const response = await this.oasisApi.getAllPages(endpoint + '?' + queryString);
            // console.log(query, queryString, response);
            this.rowData = response.data;
            this.iteration++;
            this.loading = false;
        },

        async getPaystub(payrollId, assignmentId=null, reportId=null) {
            if (!payrollId) return false;
            this.loading = true;

            let endpoint = this.type === 'employer' ? '/employee/paystub' : '/employee/paystub';
            endpoint = `${endpoint}/${payrollId}/${assignmentId}`;
            if (reportId) {
                endpoint += `/${reportId}`;
            }
            const response = await this.oasisApi.get(endpoint);
            // console.log('https://dvc.localhost/oasis/viewer/view/archive/' + response.data);
            this.link = response.data;
            this.showLink = true;
            this.loading = false;
        },

        resetSearch() {
            const now = new Date();
            const today = now.toISOString().split('T')[0];
            const startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 31).toISOString().split('T')[0];
            if (this.rowData) this.rowData = undefined;
            this.searchQueryDateStart = '2022-01-01';
            this.searchQueryDateEnd = today;
        },

    },

    created() {
        const tcFilterOrder = [
            { field: 'status_full' },
            { field: 'week_ending' },
            { field: 'employee' },
            { field: 'department' },
            { field: 'union_local' },
        ];

        this.columnDefs = [
            {
                headerName: 'Type',
                colId: 'payment_type',
                maxWidth: 200,
                minWidth: 100,
                width: 150,
                editable: false,
                sortable: true,
                valueGetter: (params) => params.data.payment_type,
                menuTabs: [],
            },
            {
                headerName: 'Date',
                colId: 'date',
                maxWidth: 200,
                minWidth: 100,
                width: 150,
                editable: false,
                sortable: true,
                cellRenderer: (params) => params.data.date ? `<span class="fakelink">${params.data.date}</span>` : '',
                valueGetter: (params) => params.data.date,
                menuTabs: [],
            },
            {
                headerName: 'Description',
                colId: 'description',
                maxWidth: 900,
                minWidth: 450,
                minWidth: 600,
                editable: false,
                sortable: true,
                valueGetter: (params) => params.data.number,
                menuTabs: [],
            },
            {
                headerName: 'Pay',
                colId: 'amount_decimal',
                maxWidth: 300,
                minWidth: 100,
                cellClass: 't-right bold',
                editable: false,
                sortable: true,
                valueGetter: (params) => parseFloat(params.data.amount_decimal).formatNumber('$'),
                menuTabs: [],
            },
        ]
    },

    mounted() {
        setTimeout(() => { $('[data-toggle="tooltip"]').tooltip() }, 500);

        // Prevent back button from failing.
        history.pushState(null, document.referrer, location.href);
        window.onpopstate = function () {
            window.location.href = document.referrer;
        };

        this.resetSearch();
        this.getPaystubs();
    }
});
</script>
